import { type Proposal, type ProposalStatus, PROPOSAL_LABELS, isLegacyProposalKind } from './types'
import { summarizeProposal } from './helpers'
import { useRouteActions } from './loadRouteActions'

export function ProposalCard({ proposal, status, error, onExecute, onDismiss }: {
  proposal: Proposal
  status: ProposalStatus
  error?: string
  onExecute: () => void
  onDismiss: () => void
}) {
  // Fallback for a kind the deployed bundle doesn't know yet: the Worker can
  // ship a new proposal kind before this ops bundle updates (deploy skew), and
  // an undefined `meta` would crash on meta.color. Render a neutral card
  // instead of white-screening the widget.
  const legacy = isLegacyProposalKind(proposal.kind)
  // Route-backed kinds: label and preview from the registry, loaded on demand.
  const routes = useRouteActions(!legacy)
  const routeKind = routes && routes.isAveryActionKind(proposal.kind) ? proposal.kind : null
  const meta = (isLegacyProposalKind(proposal.kind) ? PROPOSAL_LABELS[proposal.kind] : null) ?? (routes && routeKind ? routes.routeActionLabel(routeKind) : null) ?? {
    title: String(proposal.kind).replace(/_/g, ' '),
    verb: 'Confirm',
    color: 'var(--parkify-accent)',
  }
  const idLabel = summarizeProposal(proposal)
  const preview = routes && routeKind ? routes.routeActionPreview(proposal) : null
  return (
    <div style={{
      border: `1px solid ${status === 'done' ? '#62c584' : status === 'error' ? '#b91c1c' : 'var(--color-brand-border)'}`,
      borderRadius: 12, padding: '10px 12px',
      background: status === 'dismissed' ? 'var(--color-gray-50)' : 'var(--color-white)',
      opacity: status === 'dismissed' ? 0.5 : 1,
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: meta.color, marginBottom: 2 }}>
        ⚡ {meta.title}
      </div>
      {preview ? (
        <dl data-testid="avery-action-preview" style={{ margin: 0, fontSize: 11, color: 'var(--color-gray-800)', display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 8, rowGap: 2 }}>
          {preview.rows.map((row, i) => (
            <div key={i} style={{ display: 'contents' }}>
              <dt style={{ color: 'var(--color-gray-500)' }}>{row.label}</dt>
              <dd style={{ margin: 0, wordBreak: 'break-word' }}>
                {row.before !== undefined && (
                  <><span style={{ textDecoration: 'line-through', color: 'var(--color-gray-500)' }}>{row.before}</span>{' → '}</>
                )}
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--color-gray-800)' }}>
          <code style={{ fontFamily: 'monospace', fontSize: 10 }}>{idLabel}</code>
        </div>
      )}
      {preview?.notices?.map((notice, i) => (
        <div key={i} role="note" style={{ fontSize: 10, color: '#92400e', background: 'rgba(245,158,11,0.10)', padding: '4px 6px', borderRadius: 4, marginTop: 4 }}>
          {notice}
        </div>
      ))}
      {proposal.reason && (
        <div style={{ fontSize: 10, color: 'var(--color-gray-500)', fontStyle: 'italic', marginTop: 2 }}>
          "{proposal.reason}"
        </div>
      )}
      {/* A pending card can carry an error too: a refused Dismiss leaves it pending. */}
      {(status === 'error' || status === 'pending') && error && (
        <div style={{ fontSize: 10, color: '#b91c1c', background: 'rgba(237,28,36,0.08)', padding: '4px 6px', borderRadius: 4, marginTop: 4, fontFamily: 'monospace', wordBreak: 'break-word' }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        {status === 'pending' && (
          <>
            <button onClick={onExecute} style={{
              fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 'var(--radius-btn)',
              background: meta.color, color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {meta.verb}
            </button>
            <button onClick={onDismiss} style={{
              fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--radius-btn)',
              background: 'transparent', color: 'var(--color-gray-700)',
              border: '1px solid var(--color-brand-border)', cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Dismiss
            </button>
          </>
        )}
        {status === 'executing' && <span style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>Executing…</span>}
        {status === 'done' && <span style={{ fontSize: 11, color: '#62c584', fontWeight: 700 }}>✓ Done</span>}
        {status === 'dismissed' && <span style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>Dismissed</span>}
        {status === 'error' && (
          <button onClick={onExecute} style={{
            fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--radius-btn)',
            background: 'transparent', color: meta.color,
            border: `1px solid ${meta.color}`, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Retry
          </button>
        )}
      </div>
    </div>
  )
}
